import { useState, useCallback, useRef } from "react"
import {
  ConnectorFieldMapping,
  ConnectorDataType,
  ConnectorDirection
} from "../../../../../../types"
import {
  LEAD_FIELD_DEFINITIONS,
  SUBSCRIPTION_FIELD_DEFINITIONS,
  PENDING_SUBSCRIPTION_FIELD_DEFINITIONS,
  FieldDefinition
} from "../../../../../constants/connector-field-types"

interface UseFieldMappingProps {
  fieldMapping: ConnectorFieldMapping
  dataType: ConnectorDataType
  direction: ConnectorDirection
  onChange: (mapping: ConnectorFieldMapping) => void
}

export const useFieldMapping = ({
  fieldMapping,
  dataType,
  direction,
  onChange
}: UseFieldMappingProps) => {
  const isIncoming = direction === "incoming"
  const [newBbFieldToAdd, setNewBbFieldToAdd] = useState<string>("")
  const [newCrmFieldToAdd, setNewCrmFieldToAdd] = useState<string>("")

  // Create stable IDs for each field mapping entry using ref to avoid stale state
  const fieldIdsRef = useRef<Map<string, string>>(new Map())

  const getFieldDefinitions = (): FieldDefinition[] => {
    switch (dataType) {
      case "subscription":
        return SUBSCRIPTION_FIELD_DEFINITIONS
      case "pending_subscription":
        return PENDING_SUBSCRIPTION_FIELD_DEFINITIONS
      default:
        return LEAD_FIELD_DEFINITIONS
    }
  }

  const getFieldNames = useCallback(
    (key: string, value: any): { bbField: string; crmField: string } => {
      if (isIncoming) {
        const crmField = key
        const bbField = value?.key || ""
        return { bbField, crmField }
      } else {
        const bbField = key
        const crmField = value?.key || ""
        return { bbField, crmField }
      }
    },
    [isIncoming]
  )

  const getStableId = useCallback((key: string): string => {
    // Use the mapping key (not bbField) to ensure unique IDs
    // This handles cases where multiple CRM fields map to the same BB field
    const fieldIds = fieldIdsRef.current
    if (!fieldIds.has(key)) {
      fieldIds.set(key, `field-${Date.now()}-${Math.random()}`)
    }
    return fieldIds.get(key)!
  }, [])

  const getMappedBBFields = useCallback((): Set<string> => {
    if (isIncoming) {
      const bbFields = new Set<string>()
      Object.values(fieldMapping).forEach(val => {
        if (val?.key) {
          bbFields.add(val.key as string)
        }
      })
      return bbFields
    } else {
      return new Set(Object.keys(fieldMapping))
    }
  }, [isIncoming, fieldMapping])

  const getAvailableFields = useCallback((): FieldDefinition[] => {
    const mappedBBFields = getMappedBBFields()
    return getFieldDefinitions().filter(
      field => !mappedBBFields.has(field.value)
    )
  }, [getMappedBBFields])

  const addFieldMapping = useCallback(() => {
    // Skip if "__custom__" placeholder is still selected
    if (newBbFieldToAdd === "__custom__") {
      return
    }

    if (isIncoming) {
      if (newCrmFieldToAdd && newBbFieldToAdd) {
        const newMapping = {
          ...fieldMapping,
          [newCrmFieldToAdd]: { key: newBbFieldToAdd }
        }
        onChange(newMapping)
        setNewCrmFieldToAdd("")
        setNewBbFieldToAdd("")
      }
    } else {
      if (newBbFieldToAdd && !(newBbFieldToAdd in fieldMapping)) {
        const newMapping = { ...fieldMapping, [newBbFieldToAdd]: { key: "" } }
        onChange(newMapping)
        setNewBbFieldToAdd("")
      }
    }
  }, [isIncoming, newCrmFieldToAdd, newBbFieldToAdd, fieldMapping, onChange])

  const addCustomFieldMapping = useCallback(
    (customBbField: string) => {
      if (isIncoming) {
        if (newCrmFieldToAdd && customBbField) {
          const newMapping = {
            ...fieldMapping,
            [newCrmFieldToAdd]: { key: customBbField }
          }
          onChange(newMapping)
          setNewCrmFieldToAdd("")
          setNewBbFieldToAdd("")
        }
      } else {
        if (customBbField && !(customBbField in fieldMapping)) {
          const newMapping = { ...fieldMapping, [customBbField]: { key: "" } }
          onChange(newMapping)
          setNewBbFieldToAdd("")
        }
      }
    },
    [isIncoming, newCrmFieldToAdd, fieldMapping, onChange]
  )

  const removeFieldMapping = useCallback(
    (key: string) => {
      const newMapping = { ...fieldMapping }
      delete newMapping[key]
      // Clean up the stable ID for the removed field
      fieldIdsRef.current.delete(key)
      onChange(newMapping)
    },
    [fieldMapping, onChange]
  )

  const updateMapping = useCallback(
    (oldKey: string, newBbField: string, newCrmField: string) => {
      const entries = Object.entries(fieldMapping)
      const newMapping: any = {}

      entries.forEach(([k, v]) => {
        if (k === oldKey) {
          const newKey = isIncoming ? newCrmField : newBbField
          if (newKey) {
            // Transfer stable ID from old key to new key to prevent focus loss
            if (newKey !== oldKey && fieldIdsRef.current.has(oldKey)) {
              const stableId = fieldIdsRef.current.get(oldKey)!
              fieldIdsRef.current.delete(oldKey)
              fieldIdsRef.current.set(newKey, stableId)
            }

            // Preserve existing object structure (including values if present)
            newMapping[newKey] = isIncoming
              ? { ...v, key: newBbField }
              : { ...v, key: newCrmField }
          }
        } else {
          newMapping[k] = v
        }
      })

      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange]
  )

  return {
    isIncoming,
    newBbFieldToAdd,
    setNewBbFieldToAdd,
    newCrmFieldToAdd,
    setNewCrmFieldToAdd,
    getFieldDefinitions,
    getFieldNames,
    getStableId,
    getMappedBBFields,
    getAvailableFields,
    addFieldMapping,
    addCustomFieldMapping,
    removeFieldMapping,
    updateMapping
  }
}
